import { Component, OnDestroy, OnInit } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { SHARED_DATE_FORMATS } from '../shared/shared-date-formats.constant';
import moment from 'moment';
import { Subject, throwError, zip } from 'rxjs';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { PtoRequestService } from './pto-request.service';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { DemographicsService } from '../demographics/demographics.service';
import { Router, RouterLink } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SharedModulesModule } from '../shared/shared-modules.module';
import { SharedComponentsModule } from '../shared/shared-components.module';
import { SharedPipesModule } from '../shared/shared-pipes.module';
import { ApplicationInsightsService } from '../app-insights.service';

@Component({
  selector: 'app-pto-request',
  templateUrl: './pto-request.component.html',
  styleUrls: ['./pto-request.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: SHARED_DATE_FORMATS,
    },
  ],
  imports: [
    SharedModulesModule,
    SharedComponentsModule,
    SharedPipesModule,
    RouterLink,
  ],
})
export class PtoRequestComponent implements OnInit, OnDestroy {

  private unsubscriber: Subject<void> = new Subject<void>();
  ptoForm: UntypedFormGroup;
  loading = false;
  user;
  userProfile;
  isPartTime = false;
  payPeriod = {
    pay_period_start_date: '',
    pay_period_end_date: '',
    isSet: false,
  };
  message;

  weekendsDatesFilter = (d: Date): boolean => {
    const day = (new Date(d)).getDay();
    return !this.ptoForm.value.excludeWeekend || day !== 0 && day !== 6;
  };

  constructor(public fb: UntypedFormBuilder,
    public ptoService: PtoRequestService,
    public demoService: DemographicsService,
    private appInsights: ApplicationInsightsService,
    private router: Router,
    private snackbar: MatSnackBar) { }

  ngOnInit(): void {
    this.loading = true;
    this.demoService.getUserData().pipe(
      takeUntil(this.unsubscriber),
      catchError(err => {
        this.appInsights.logEvent('PTO Request Get User Data GET_ERROR', { 
          error: err, user_id: this.user.employee_id });
        return throwError(() => err);
      })).subscribe(user => {
        this.user = user;
        this.appInsights.logPageView('PTO Request', this.router.url, { user_id: this.user.employee_id });
        zip(this.demoService.getProfile(user.employee_id), this.ptoService.getCurrentPayPeriod(user.employee_id)).pipe(
        takeUntil(this.unsubscriber),
        catchError(err => {
          this.appInsights.logEvent('PTO Request Get Employee Profile GET_ERROR', { 
            error: err, user_id: this.user.employee_id });
          return throwError(() => err);
        }),
        finalize(() => this.loading = false)).subscribe(([profile, pay_period]) => {
          this.userProfile = profile;
          this.isPartTime = (profile.status_description == 'Active Part Time Hourly' || profile.status_description == 'Active Part Time Salaried');
          this.payPeriod.pay_period_start_date = moment(pay_period.pay_period_start_date).format(SHARED_DATE_FORMATS.parse.payPeriod);
          this.payPeriod.pay_period_end_date = moment(pay_period.pay_period_end_date).format(SHARED_DATE_FORMATS.parse.payPeriod);
          this.payPeriod.isSet = true;
          this.setupPTOForm();
        });
      });
  }

  setupPTOForm() {
    this.ptoForm = this.fb.group({
      ptoDate: ['', Validators.required],
      range: this.fb.group({
        startDate: [null, Validators.required],
        endDate: [null, Validators.required],
      }),
      hours: ['', Validators.required],
      minutes: ['', Validators.required],
      dayTotal: { value: 0, disabled: true },
      note: '',
      isRange: false,
      excludeWeekend: false,
    }, { validators: [payPeriodValidator(new Date(this.payPeriod.pay_period_end_date)), ptoBalanceValidator(this.userProfile.ptoBalance)] });
    this.ptoForm.get('range').disable();
  }

  toggleIsRange(): void {
    if (this.ptoForm.get('isRange').value) {
      this.ptoForm.get('ptoDate').disable();
      this.ptoForm.get('range').enable();
    } else {
      this.ptoForm.get('range').disable();
      this.ptoForm.get('ptoDate').enable();
    }
  }

  calcDayTotal(): void {
    let total = 0;
    const startDate = new Date(this.ptoForm.get('range').get('startDate').value);
    const endDate = new Date(this.ptoForm.get('range').get('endDate').value);

    while (startDate <= endDate) {
      if (!this.ptoForm.value.excludeWeekend || (startDate.getDay() != 0 && startDate.getDay() != 6)) {
        total++;
      }
      startDate.setDate(startDate.getDate() + 1);
    }
    this.ptoForm.get('dayTotal').setValue(total);
  }

  onSubmit(form: UntypedFormGroup): void {
    this.loading = true;
    const ptoHoursMins = Number(form.value.minutes) > 1 ? Number(form.value.hours + '.5') : Number(form.value.hours);

    const ptoDate = !form.value.isRange ? form.value.ptoDate?.format(SHARED_DATE_FORMATS.parse.dbInput) : null;
    const start = form.value.isRange ? form.get('range').value.startDate?.format(SHARED_DATE_FORMATS.parse.dbInput) : null;
    const end = form.value.isRange ? form.get('range').value.endDate?.format(SHARED_DATE_FORMATS.parse.dbInput) : null;

    if (this.payPeriod.isSet) {
      const data = form.value.isRange ?
        {
          employee_id: this.user.employee_id,
          PTORequestStartDate: start,
          PTORequestEndDate: end,
          PTORequestExcludeWeekends: form.value.excludeWeekend,
          PTORequestHours: ptoHoursMins,
          PTORequestNote: form.value.note,
        } :
        {
          employee_id: this.user.employee_id,
          PTORequestDate: ptoDate,
          PTORequestHours: ptoHoursMins,
          PTORequestNote: form.value.note,
        };

      // set message
      this.message = 'Saving...';

      this.ptoService.save(data, form.value.isRange).pipe(
        takeUntil(this.unsubscriber),
        finalize(() => { this.loading = false; this.message = null; }),
        catchError(err => {
            this.appInsights.logEvent('PTO Request Create POST_ERROR', { 
              error: err, user_id: this.user.employee_id });
            this.snackbar.open('PTO Request ERROR', 'Dismiss');
            return throwError(() => err);
          })
      ).subscribe((r) => {
          this.appInsights.logEvent('PTO Request Create POST', { 
            user_id: this.user.employee_id });
          this.snackbar.open('PTO Request Success', 'Dismiss');
          this.setupPTOForm();
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }
}

// Verify Date is not during the current Pay Period
function payPeriodValidator(periodEnd: Date): ValidatorFn {
  return (c: AbstractControl): { [key: string]: boolean } | null => {
    let ptoDate = null;
    if (c.get('isRange').value && c.get('range').get('startDate').value) {
      ptoDate = new Date(c.get('range').get('startDate').value);
    } else if (c.get('ptoDate').value) {
      ptoDate = new Date(c.get('ptoDate').value);
    }
    const valid = ptoDate ? periodEnd < ptoDate : true;
    return !valid ? { 'payPeriod': true } : null;
  };
}

// Verify PTO Total <= PTO Balance
function ptoBalanceValidator(ptoBalance: number): ValidatorFn {
  return (c: AbstractControl): { [key: string]: boolean } | null => {
    let valid = false;

    // If Is Range, find number of PTO Days and multiply by hours and compare to ptoBalance
    if (c.get('isRange').value) {
      valid = c.get('dayTotal').value * Number(c.get('hours').value) <= ptoBalance;
      // If Is Not Range, simply compare hours to ptoBalance
    } else {
      valid = Number(c.get('hours').value) <= ptoBalance;
    }
    return !valid ? { 'ptoBalance': true } : null;
  };
}
